import {
  activeCallListSchema,
  aiTelephonySettingsSchema,
  clickToCallResultSchema,
  communicationLogListResponseSchema,
  communicationLogSchema,
  globalTelephonySettingsSchema,
  pendingSalesCallOutcomeListSchema,
  screenPopSchema,
  telephonyConnectionTestSchema,
  telephonyPipelineTestSchema,
  userTelephonyConfigListSchema,
  userTelephonyConfigSchema,
  type AiActionItem,
  type AiTelephonySettings,
  type AiTelephonySettingsUpdateValues,
  type ClickToCallInput,
  type ClickToCallResult,
  type CommunicationLog,
  type CommunicationLogAiSummaryUpdateInput,
  type CommunicationLogListResponse,
  type CommunicationLogNotesUpdateInput,
  type CommunicationLogScope,
  type GlobalTelephonySettings,
  type GlobalTelephonySettingsUpdateValues,
  type PendingSalesCallOutcome,
  type ScreenPop,
  type TelephonyConnectionTest,
  type TelephonyPipelineTest,
  type UserTelephonyConfig,
  type UserTelephonyConfigList,
  type UserTelephonyConfigUpdateValues,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiFetch, type ApiError } from '@/lib/api-client';
import { useFeatureAvailability } from '@/features/platform-features';

export interface CallLogListParams {
  page: number;
  pageSize: number;
  scope: CommunicationLogScope;
  partnerId?: string | undefined;
  q?: string | undefined;
}

export const telephonyKeys = {
  all: ['telephony'] as const,
  logs: (params: CallLogListParams) => ['telephony', 'logs', params] as const,
  log: (id: string) => ['telephony', 'log', id] as const,
  activeCalls: ['telephony', 'active-calls'] as const,
  pendingSalesOutcomes: ['telephony', 'pending-sales-outcomes'] as const,
  screenPop: (phoneNumber: string) => ['telephony', 'screen-pop', phoneNumber] as const,
  me: ['telephony', 'me'] as const,
  userConfigs: ['telephony', 'user-configs'] as const,
  aiSettings: ['telephony', 'ai-settings'] as const,
  globalSettings: ['telephony', 'global-settings'] as const,
};

function searchParams(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  return search.toString();
}

// ---------------------------------------------------------------------------
// Gesprächsprotokoll
// ---------------------------------------------------------------------------

export function useCallLogs(
  params: CallLogListParams,
  enabled = true,
): UseQueryResult<CommunicationLogListResponse, ApiError> {
  return useQuery({
    queryKey: telephonyKeys.logs(params),
    queryFn: () =>
      apiFetch(
        `/api/v1/telephony/logs?${searchParams({ ...params })}`,
        communicationLogListResponseSchema,
      ),
    enabled,
  });
}

export function useActiveCalls(enabled: boolean): UseQueryResult<CommunicationLog[], ApiError> {
  return useQuery({
    queryKey: telephonyKeys.activeCalls,
    queryFn: async () => {
      const result = await apiFetch('/api/v1/telephony/active-calls', activeCallListSchema);
      return result.data;
    },
    enabled,
    // Ein eingehender Ruf soll ohne Zutun des Benutzers erscheinen.
    refetchInterval: 5000,
  });
}

export function usePendingSalesCallOutcomes(
  enabled: boolean,
): UseQueryResult<PendingSalesCallOutcome[], ApiError> {
  return useQuery({
    queryKey: telephonyKeys.pendingSalesOutcomes,
    queryFn: async () => {
      const result = await apiFetch(
        '/api/v1/telephony/pending-sales-outcomes',
        pendingSalesCallOutcomeListSchema,
      );
      return result.data;
    },
    enabled,
    refetchInterval: 4000,
  });
}

export function useScreenPop(phoneNumber: string | null): UseQueryResult<ScreenPop, ApiError> {
  return useQuery({
    queryKey: telephonyKeys.screenPop(phoneNumber ?? 'none'),
    queryFn: () =>
      apiFetch(
        `/api/v1/telephony/screen-pop?${searchParams({ phoneNumber: phoneNumber ?? '' })}`,
        screenPopSchema,
      ),
    enabled: phoneNumber !== null,
  });
}

export function useUpdateCallNotes(): UseMutationResult<
  CommunicationLog,
  ApiError,
  { id: string } & CommunicationLogNotesUpdateInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }) =>
      apiFetch(`/api/v1/telephony/logs/${id}/notes`, communicationLogSchema, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all });
    },
  });
}

export function useUpdateAiSummary(): UseMutationResult<
  CommunicationLog,
  ApiError,
  { id: string } & CommunicationLogAiSummaryUpdateInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }) =>
      apiFetch(`/api/v1/telephony/logs/${id}/ai-summary`, communicationLogSchema, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all });
    },
  });
}

export function useUpdateActionItems(): UseMutationResult<
  CommunicationLog,
  ApiError,
  { id: string; aiActionItems: AiActionItem[] }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, aiActionItems }) =>
      apiFetch(`/api/v1/telephony/logs/${id}/action-items`, communicationLogSchema, {
        method: 'PATCH',
        body: { aiActionItems },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all });
    },
  });
}

export function useReprocessCall(): UseMutationResult<CommunicationLog, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) =>
      apiFetch(`/api/v1/telephony/logs/${id}/reprocess`, communicationLogSchema, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Click-to-Call
// ---------------------------------------------------------------------------

export function useClickToCall(): UseMutationResult<ClickToCallResult, ApiError, ClickToCallInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch('/api/v1/telephony/click-to-call', clickToCallResultSchema, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Konfiguration
// ---------------------------------------------------------------------------

export function useMyTelephonyConfig(
  enabled: boolean,
): UseQueryResult<UserTelephonyConfig, ApiError> {
  const features = useFeatureAvailability();
  const available = enabled && features.isEnabled('telephony_ringover');
  return useQuery({
    queryKey: [...telephonyKeys.me, available],
    queryFn: () => apiFetch('/api/v1/telephony/me', userTelephonyConfigSchema),
    enabled: available,
  });
}

export function useUserTelephonyConfigs(): UseQueryResult<UserTelephonyConfigList, ApiError> {
  return useQuery({
    queryKey: telephonyKeys.userConfigs,
    queryFn: () => apiFetch('/api/v1/telephony/user-configs', userTelephonyConfigListSchema),
  });
}

export function useUpdateUserTelephonyConfig(): UseMutationResult<
  UserTelephonyConfig,
  ApiError,
  { userId: string; input: UserTelephonyConfigUpdateValues }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, input }) =>
      apiFetch(`/api/v1/telephony/user-configs/${userId}`, userTelephonyConfigSchema, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all });
    },
  });
}

export function useAiTelephonySettings(): UseQueryResult<AiTelephonySettings, ApiError> {
  return useQuery({
    queryKey: telephonyKeys.aiSettings,
    queryFn: () => apiFetch('/api/v1/telephony/ai-settings', aiTelephonySettingsSchema),
  });
}

export function useGlobalTelephonySettings(): UseQueryResult<GlobalTelephonySettings, ApiError> {
  return useQuery({
    queryKey: telephonyKeys.globalSettings,
    queryFn: () =>
      apiFetch('/api/v1/admin/telephony/global-settings', globalTelephonySettingsSchema),
  });
}

export function useUpdateGlobalTelephonySettings(): UseMutationResult<
  GlobalTelephonySettings,
  ApiError,
  GlobalTelephonySettingsUpdateValues
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) =>
      apiFetch('/api/v1/admin/telephony/global-settings', globalTelephonySettingsSchema, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.globalSettings });
    },
  });
}

/** Live-Aufruf beim Anbieter; das Ergebnis wird bewusst nicht zwischengespeichert. */
export function useTestUserConnection(): UseMutationResult<
  TelephonyConnectionTest,
  ApiError,
  string
> {
  return useMutation({
    mutationFn: (userId) =>
      apiFetch(
        `/api/v1/admin/telephony/users/${userId}/test-connection`,
        telephonyConnectionTestSchema,
        { method: 'POST' },
      ),
  });
}

export function useUpdateAiTelephonySettings(): UseMutationResult<
  AiTelephonySettings,
  ApiError,
  AiTelephonySettingsUpdateValues
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      apiFetch('/api/v1/telephony/ai-settings', aiTelephonySettingsSchema, {
        method: 'PUT',
        body,
      }),
    onSuccess: (settings) => {
      queryClient.setQueryData(telephonyKeys.aiSettings, settings);
    },
  });
}

export function useTestAiPipeline(): UseMutationResult<TelephonyPipelineTest, ApiError, File> {
  return useMutation({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append('file', file);

      return apiFetch('/api/v1/telephony/ai-settings/test', telephonyPipelineTestSchema, {
        method: 'POST',
        formData,
      });
    },
  });
}
